import { baseApi } from "@/services/base-api";
import type { Category } from "@/features/marketplace/types/category";
import type { Product } from "@/features/marketplace/types/product";
import type { SearchFilters } from "@/features/marketplace/types/search";

/**
 * Endpoint paths match docs/api/api-design.md exactly: `GET /products`,
 * `GET /categories` — both listed there as the canonical public,
 * resource-based routes. `searchProducts` reuses the same `/products`
 * endpoint with query params rather than a separate `/search` route —
 * matches docs/ui/routes.md's own decision that `/search` is a query-param
 * view over the product list, not a distinct resource.
 */
export const marketplaceApi = {
  getFeaturedProducts: () =>
    baseApi.get<Product[]>("/products", { params: { sort: "newest", limit: 12 } }),

  getCategories: () => baseApi.get<Category[]>("/categories"),

  /**
   * docs/api/api-design.md shows `GET /products/:id`; this fetches by
   * `:slug` instead, matching docs/ui/routes.md's public, SEO-friendly
   * `/product/:slug` route — a reasonable extension of the documented
   * endpoint, not a literal match.
   */
  getProductBySlug: (slug: string) => baseApi.get<Product>(`/products/${slug}`),

  searchProducts: (filters: SearchFilters) =>
    baseApi.getPaginated<Product>("/products", {
      params: {
        q: filters.q || undefined,
        category: filters.category ?? undefined,
        minPrice: filters.minPrice ?? undefined,
        maxPrice: filters.maxPrice ?? undefined,
        condition: filters.conditions.length > 0 ? filters.conditions.join(",") : undefined,
        sort: filters.sort,
        page: filters.page,
      },
    }),
};
